/* ============================================================
   Kawish by JB — Main Theme JS
   Interactions: Nav scroll, mobile nav toggle, scroll reveals,
   newsletter form validation & submit
   ============================================================ */

'use strict';

// ── Dynamic Announcement Bar Height ──────────────────────
(function () {
  const announceBar = document.querySelector('.announce-strip');
  function syncAnnounceHeight() {
    const h = announceBar ? announceBar.offsetHeight : 0;
    document.documentElement.style.setProperty('--announce-h', h + 'px');
  }
  syncAnnounceHeight();
  window.addEventListener('resize', syncAnnounceHeight, { passive: true });
})();

// ── Media Banner Slider ───────────────────────────────────
(function () {
  document.querySelectorAll('.media-banner-section.has-slides').forEach(function (section) {
    const slider  = section.querySelector('.media-banner-slider');
    const track   = section.querySelector('.media-banner-track');
    if (!slider || !track) return;

    const slides   = Array.from(track.querySelectorAll('.media-banner-slide'));
    const dots     = Array.from(section.querySelectorAll('.banner-dot'));
    const prevBtn  = section.querySelector('.banner-arrow--prev');
    const nextBtn  = section.querySelector('.banner-arrow--next');
    const doAutoplay  = slider.dataset.autoplay === 'true';
    const speedMs  = parseInt(slider.dataset.speed || '5', 10) * 1000;

    let current = 0;
    let timer   = null;

    function goTo(index) {
      // deactivate current
      slides[current].classList.remove('is-active');
      if (dots[current]) {
        dots[current].classList.remove('is-active');
        dots[current].setAttribute('aria-selected', 'false');
      }

      // activate next
      current = (index + slides.length) % slides.length;
      slides[current].classList.add('is-active');
      if (dots[current]) {
        dots[current].classList.add('is-active');
        dots[current].setAttribute('aria-selected', 'true');
      }
    }

    function next() { goTo(current + 1); }
    function prev() { goTo(current - 1); }

    function startAutoplay() {
      if (!doAutoplay) return;
      clearInterval(timer);
      timer = setInterval(next, speedMs);
    }

    function resetAndPlay() {
      clearInterval(timer);
      startAutoplay();
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { prev(); resetAndPlay(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { next(); resetAndPlay(); });

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { goTo(i); resetAndPlay(); });
    });

    // Touch / swipe support
    let touchStartX = 0;
    track.addEventListener('touchstart', function (e) {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });
    track.addEventListener('touchend', function (e) {
      const diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 48) {
        diff > 0 ? next() : prev();
        resetAndPlay();
      }
    });

    // Pause on hover
    section.addEventListener('mouseenter', function () { clearInterval(timer); });
    section.addEventListener('mouseleave', startAutoplay);

    startAutoplay();
  });
})();

// ── Nav Scroll State ─────────────────────────────────────
(function () {
  const header = document.getElementById('site-header');
  if (!header) return;

  let ticking = false;
  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(() => {
        header.classList.toggle('scrolled', window.scrollY > 20);
        ticking = false;
      });
      ticking = true;
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
})();

// ── Mobile Nav Toggle ────────────────────────────────────
(function () {
  const toggle = document.getElementById('nav-toggle');
  const nav    = document.getElementById('primary-nav');
  if (!toggle || !nav) return;

  let isOpen = false;

  function open() {
    isOpen = true;
    nav.classList.add('open');
    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-label', 'Close navigation menu');
    document.body.style.overflow = 'hidden';

    const lines = toggle.querySelectorAll('.hamburger-line');
    if (lines[0]) lines[0].style.transform = 'translateY(6px) rotate(45deg)';
    if (lines[1]) lines[1].style.opacity = '0';
    if (lines[2]) lines[2].style.transform = 'translateY(-6px) rotate(-45deg)';
  }

  function close() {
    isOpen = false;
    nav.classList.remove('open');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Open navigation menu');
    document.body.style.overflow = '';

    const lines = toggle.querySelectorAll('.hamburger-line');
    if (lines[0]) lines[0].style.transform = '';
    if (lines[1]) lines[1].style.opacity = '';
    if (lines[2]) lines[2].style.transform = '';
  }

  toggle.addEventListener('click', () => isOpen ? close() : open());

  nav.querySelectorAll('.nav-link').forEach(link => {
    link.addEventListener('click', close);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) close();
  });

  window.addEventListener('resize', () => {
    if (window.innerWidth > 640 && isOpen) close();
  }, { passive: true });
})();

// ── Scroll Reveal ────────────────────────────────────────
(function () {
  const opts = { threshold: 0.1, rootMargin: '0px 0px -40px 0px' };
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      }
    });
  }, opts);

  const targets = [
    { selector: '.section-header',      cls: 'reveal' },
    { selector: '.categories-grid',     cls: 'reveal-stagger' },
    { selector: '.products-grid',       cls: 'reveal-stagger' },
    { selector: '.story-content',       cls: 'reveal' },
    { selector: '.reviews-grid',        cls: 'reveal-stagger' },
    { selector: '.newsletter-content',  cls: 'reveal' },
  ];

  targets.forEach(({ selector, cls }) => {
    document.querySelectorAll(selector).forEach(el => {
      el.classList.add(cls);
      observer.observe(el);
    });
  });
})();

// ── Newsletter Form ──────────────────────────────────────
(function () {
  const form    = document.getElementById('newsletter-form');
  const input   = document.getElementById('email-input');
  const success = document.getElementById('form-success');
  const error   = document.getElementById('form-error');
  if (!form || !input) return;

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  form.addEventListener('submit', (e) => {
    const email = input.value.trim();
    if (!EMAIL_RE.test(email)) {
      e.preventDefault();
      if (error) {
        error.textContent = 'Please enter a valid email address.';
        error.hidden = false;
      }
      if (success) success.hidden = true;
      input.setAttribute('aria-invalid', 'true');
    }
  });

  input.addEventListener('input', () => {
    if (input.getAttribute('aria-invalid') === 'true') {
      if (error) error.hidden = true;
      input.removeAttribute('aria-invalid');
    }
  });
})();

// ── Custom Popup Announcement (Shopify Theme) ─────────────
(function () {
  const modal       = document.getElementById('announcement-modal');
  const overlay     = document.getElementById('announcement-modal-overlay');
  const closeBtn    = document.getElementById('announcement-modal-close');
  const dismissBtn  = document.getElementById('announcement-dismiss-link');
  const dontShowChk = document.getElementById('announcement-dont-show-checkbox');

  if (!modal || !overlay) return;

  const STORAGE_KEY = 'kawish_announcement_dismissed_until';
  const delayMs     = parseInt(modal.dataset.delay || '1500', 10);
  const expiryDays  = parseInt(modal.dataset.expiryDays || '1', 10);

  function isDismissed() {
    const dismissedUntil = localStorage.getItem(STORAGE_KEY);
    if (!dismissedUntil) return false;
    return Date.now() < parseInt(dismissedUntil, 10);
  }

  function openPopup() {
    if (isDismissed()) return;

    setTimeout(() => {
      modal.classList.add('open');
      overlay.classList.add('open');
      modal.setAttribute('aria-hidden', 'false');
      overlay.setAttribute('aria-hidden', 'false');
      const input = modal.querySelector('input[type="email"]');
      if (input) input.focus();
    }, delayMs);
  }

  function closePopup() {
    modal.classList.remove('open');
    overlay.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('aria-hidden', 'true');

    if (dontShowChk && dontShowChk.checked) {
      const expiry = Date.now() + (expiryDays * 24 * 60 * 60 * 1000);
      localStorage.setItem(STORAGE_KEY, expiry.toString());
    }
  }

  if (closeBtn) closeBtn.addEventListener('click', closePopup);
  if (dismissBtn) dismissBtn.addEventListener('click', closePopup);
  if (overlay) overlay.addEventListener('click', closePopup);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('open')) {
      closePopup();
    }
  });

  openPopup();

  window.KawishAnnouncement = {
    show: function () {
      localStorage.removeItem(STORAGE_KEY);
      openPopup();
    },
    hide: closePopup,
    resetDismissal: function () {
      localStorage.removeItem(STORAGE_KEY);
    }
  };
})();

